import React, { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Tag } from "../../../types";

interface EditingTag extends Tag {
  newName?: string;
}

interface TagsSettingsProps {
  tags: Tag[];
  getTagTaskCount: (tagId: string) => number;
  editingTag: EditingTag | null;
  setEditingTag: (tag: EditingTag | null) => void;
  onUpdateTag: (id: string, newName: string, color: string) => void;
  onDeleteClick: (tag: Tag) => void;
  onAddTag: () => void;
}

interface TagRowProps {
  tag: Tag;
  getTagTaskCount: (tagId: string) => number;
  editingTag: EditingTag | null;
  setEditingTag: (tag: EditingTag | null) => void;
  onUpdateTag: (id: string, newName: string, color: string) => void;
  onDeleteClick: (tag: Tag) => void;
}

const TagRow: React.FC<TagRowProps> = ({
  tag,
  getTagTaskCount,
  editingTag,
  setEditingTag,
  onUpdateTag,
  onDeleteClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`px-3 py-2.5 transition-colors ${isHovered ? "bg-warm-border-subtle/50" : ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {editingTag?.id === tag.id ? (
        <div className="space-y-2.5">
          <div className="flex gap-2 items-center">
            <input
              type="color"
              value={editingTag.color}
              onChange={(e) =>
                setEditingTag({
                  ...editingTag,
                  color: e.target.value,
                })
              }
              className="w-8 h-8 rounded-lg cursor-pointer border border-warm-border flex-shrink-0"
            />
            <input
              type="text"
              value={editingTag.newName ?? editingTag.name}
              onChange={(e) =>
                setEditingTag({
                  ...editingTag,
                  newName: e.target.value,
                })
              }
              className="flex-1 bg-warm-bg-elevated border border-warm-border rounded-lg px-3 py-1.5 text-sm text-warm-text-primary focus:outline-none focus:ring-2 focus:ring-warm-blue"
            />
            <span
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white flex-shrink-0"
              style={{ backgroundColor: editingTag.color }}
            >
              {editingTag.newName ?? editingTag.name}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setEditingTag(null)}
              className="flex-1 px-3 py-1.5 bg-warm-bg-elevated hover:bg-warm-border-subtle text-warm-text-primary rounded-lg text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() =>
                onUpdateTag(
                  tag.id,
                  editingTag.newName ?? editingTag.name,
                  editingTag.color
                )
              }
              className="flex-1 px-3 py-1.5 bg-warm-blue hover:bg-warm-blue-dark text-white rounded-lg text-sm transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
              style={{ backgroundColor: tag.color }}
            >
              {tag.name}
            </span>
            <span className="text-xs text-warm-text-tertiary bg-warm-border-subtle px-1.5 py-0.5 rounded-full tabular-nums">
              {getTagTaskCount(tag.id)}
            </span>
          </div>
          <div className={`flex items-center gap-0.5 transition-opacity duration-150 ${isHovered ? "opacity-100" : "opacity-0"}`}>
            <button
              onClick={() => setEditingTag({ ...tag, newName: tag.name })}
              aria-label="Edit"
              className="p-1.5 rounded-md text-warm-text-tertiary hover:text-warm-text-primary hover:bg-warm-border-subtle transition-colors"
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={() => onDeleteClick(tag)}
              aria-label="Delete"
              className="p-1.5 rounded-md text-warm-text-tertiary hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const TagsSettings: React.FC<TagsSettingsProps> = ({
  tags,
  getTagTaskCount,
  editingTag,
  setEditingTag,
  onUpdateTag,
  onDeleteClick,
  onAddTag,
}) => {
  return (
    <div>
      <h3 className="text-lg font-semibold text-warm-text-primary mb-4">Tags</h3>
      <div className="rounded-xl border border-warm-border bg-warm-bg-elevated divide-y divide-warm-border mb-4 overflow-hidden">
        {tags.length === 0 ? (
          <p className="text-warm-text-secondary text-center py-6 text-sm">
            No tags yet. Create your first tag!
          </p>
        ) : (
          tags.map((tag) => (
            <TagRow
              key={tag.id}
              tag={tag}
              getTagTaskCount={getTagTaskCount}
              editingTag={editingTag}
              setEditingTag={setEditingTag}
              onUpdateTag={onUpdateTag}
              onDeleteClick={onDeleteClick}
            />
          ))
        )}
      </div>

      <button
        onClick={onAddTag}
        className="w-full px-4 py-2 bg-warm-blue hover:bg-warm-blue-dark text-white rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
      >
        <Plus size={16} />
        Add New Tag
      </button>
    </div>
  );
};

export default TagsSettings;
